/**
 * WebAuthn Plugin for Nuxt 2
 * Provides utility functions for WebAuthn/FIDO2 authentication
 * Compatible with CVing backend API
 */

/**
 * Convert ArrayBuffer to base64url string
 * @param {ArrayBuffer} buffer
 * @returns {string}
 */
export function base64urlEncode(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  const base64 = btoa(binary)
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/**
 * Convert base64url string to ArrayBuffer
 * @param {string} base64url
 * @returns {Uint8Array}
 */
export function base64urlDecode(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

/**
 * Check if the browser is an in-app WebView (WhatsApp, Facebook, Instagram, etc.)
 * These WebViews often don't support WebAuthn properly
 * @returns {boolean}
 */
export function isInAppBrowser() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false
  }

  const ua = navigator.userAgent || navigator.vendor || ''

  // Common in-app browser indicators
  const inAppIndicators = [
    // WhatsApp
    /WhatsApp/i,
    /WAiOS/i, // WhatsApp iOS WebView [WAiOS/x.x.x]
    /WAAndroid/i, // WhatsApp Android WebView
    /wvwhatsapp/i,
    // Facebook
    /FBAN/i,
    /FBAV/i,
    /FB_IAB/i,
    /FB4A/i,
    // Instagram
    /Instagram/i,
    // Twitter/X
    /Twitter/i,
    // LinkedIn
    /LinkedInApp/i,
    // LINE
    /\bLine\//i,
    // Snapchat
    /Snapchat/i,
    // TikTok
    /TikTok/i,
    /BytedanceWebview/i,
    // Telegram
    /TelegramBot/i,
    // WeChat
    /MicroMessenger/i,
    // Generic WebView indicators
    /\bwv\b/i, // Android WebView marker
    /WebView/i
  ]

  // Check for in-app browser indicators
  for (const pattern of inAppIndicators) {
    if (pattern.test(ua)) {
      return true
    }
  }

  // iOS specific: Check if it's a WebView (not Safari or Chrome)
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
  if (isIOS) {
    // In iOS, if it's not Safari and not Chrome, it's likely a WebView
    const isSafari = /Safari/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua)
    const isChrome = /CriOS/.test(ua)
    const isFirefox = /FxiOS/.test(ua)

    // If user agent doesn't contain Safari, Chrome, or Firefox, it's likely a WebView
    if (!isSafari && !isChrome && !isFirefox) {
      return true
    }
  }

  return false
}

/**
 * Check if WebAuthn is supported by the browser
 * @returns {boolean}
 */
export function isWebAuthnAvailable() {
  return (
    typeof window !== 'undefined' &&
    window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential === 'function'
  )
}

/**
 * Check if Conditional Mediation (Passkey Autofill) is supported by the browser
 * @returns {Promise<boolean>}
 */
export async function isConditionalMediationSupported() {
  if (!isWebAuthnAvailable()) {
    return false
  }
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable()
  } catch (error) {
    return false
  }
}

/**
 * WebAuthn error types with user-friendly messages
 */
export const WebAuthnErrors = {
  NotAllowedError: 'webauthn.errors.notAllowed', // User cancelled or timeout
  SecurityError: 'webauthn.errors.security', // Not HTTPS or invalid domain
  NotSupportedError: 'webauthn.errors.notSupported', // Browser doesn't support WebAuthn
  InvalidStateError: 'webauthn.errors.invalidState', // Credential already registered
  UnknownError: 'webauthn.errors.unknown', // Generic error
  NetworkError: 'webauthn.errors.network', // Network/API error
  AbortError: 'webauthn.errors.abort' // Operation aborted
}

/**
 * Get user-friendly error message key for i18n
 * @param {Error} error
 * @returns {string}
 */
export function getWebAuthnErrorKey(error) {
  if (!error) {
    return WebAuthnErrors.UnknownError
  }

  const errorName = error.name || error.constructor?.name || ''
  switch (errorName) {
  case 'NotAllowedError':
    return WebAuthnErrors.NotAllowedError
  case 'SecurityError':
    return WebAuthnErrors.SecurityError
  case 'NotSupportedError':
    return WebAuthnErrors.NotSupportedError
  case 'InvalidStateError':
    return WebAuthnErrors.InvalidState
  case 'AbortError':
    return WebAuthnErrors.AbortError
  default:
    return WebAuthnErrors.UnknownError
  }
}

/**
 * Prepare credential creation options from backend response
 * @param {Object} options - Options from /webauthn/register/begin
 * @returns {Object} - Parsed options ready for navigator.credentials.create()
 */
export function prepareCredentialCreationOptions(options) {
  const publicKey = options.publicKey

  return {
    publicKey: {
      ...publicKey,
      challenge: base64urlDecode(publicKey.challenge),
      user: {
        ...publicKey.user,
        id: base64urlDecode(publicKey.user.id)
      },
      excludeCredentials: publicKey.excludeCredentials?.map(cred => ({
        ...cred,
        id: base64urlDecode(cred.id)
      })) || []
    }
  }
}

/**
 * Prepare credential request options from backend response
 * @param {Object} options - Options from /webauthn/login/begin
 * @returns {Object} - Parsed options ready for navigator.credentials.get()
 */
export function prepareCredentialRequestOptions(options) {
  const publicKey = options.publicKey

  return {
    publicKey: {
      ...publicKey,
      challenge: base64urlDecode(publicKey.challenge),
      allowCredentials: publicKey.allowCredentials?.map(cred => ({
        ...cred,
        id: base64urlDecode(cred.id)
      })) || []
    }
  }
}

/**
 * Format credential for registration finish endpoint
 * @param {PublicKeyCredential} credential
 * @returns {Object}
 */
export function formatRegistrationCredential(credential) {
  return {
    id: credential.id,
    rawId: base64urlEncode(credential.rawId),
    response: {
      attestationObject: base64urlEncode(credential.response.attestationObject),
      clientDataJSON: base64urlEncode(credential.response.clientDataJSON)
    },
    type: credential.type
  }
}

/**
 * Format assertion for login finish endpoint
 * @param {PublicKeyCredential} assertion
 * @returns {Object}
 */
export function formatLoginAssertion(assertion) {
  return {
    id: assertion.id,
    rawId: base64urlEncode(assertion.rawId),
    response: {
      authenticatorData: base64urlEncode(assertion.response.authenticatorData),
      clientDataJSON: base64urlEncode(assertion.response.clientDataJSON),
      signature: base64urlEncode(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? base64urlEncode(assertion.response.userHandle)
        : null
    },
    type: assertion.type
  }
}

/**
 * WebAuthn Service - Main class for handling WebAuthn operations
 */
export class WebAuthnService {
  constructor(axios, auth) {
    this.$axios = axios
    this.$auth = auth
    this.conditionalAbortController = null
  }

  /**
   * Get current user ID for localStorage keys
   * @returns {string|null}
   */
  getUserId() {
    if (typeof window === 'undefined') {
      return null
    }
    return this.$auth?.user?.uid || null
  }

  /**
   * Get localStorage key with user ID suffix
   * @param {string} baseKey
   * @returns {string}
   */
  getStorageKey(baseKey) {
    const userId = this.getUserId()
    return userId ? `${baseKey}_${userId}` : baseKey
  }

  /**
   * Check if Conditional Mediation (Passkey Autofill) is supported
   * @returns {Promise<boolean>}
   */
  async isConditionalMediationSupported() {
    return await isConditionalMediationSupported()
  }

  /**
   * Start conditional login (Passkey Autofill)
   * This should be called on page load to enable passkey suggestions in the email field
   * @returns {Promise<Object>}
   */
  async startConditionalLogin() {
    try {
      // Abort any existing conditional request
      this.abortConditionalLogin()

      // Create new abort controller
      this.conditionalAbortController = new AbortController()

      // Step 1: Begin discoverable login (no email required)
      const beginResponse = await this.$axios.post('/proxyapi/webauthn/login/begin-discoverable')
      const { options, session_id: sessionId } = beginResponse.data

      // Step 2: Get assertion with conditional mediation
      const parsedOptions = prepareCredentialRequestOptions(options)
      const assertion = await navigator.credentials.get({
        ...parsedOptions,
        mediation: 'conditional',
        signal: this.conditionalAbortController.signal
      })

      if (!assertion) {
        throw new Error('Failed to get assertion')
      }

      // Step 3: Finish discoverable login (user identified by userHandle)
      // Pass session_id as header X-Session-ID (required by backend)
      const assertionData = formatLoginAssertion(assertion)
      const finishResponse = await this.$axios.post(
        '/proxyapi/webauthn/login/finish-discoverable',
        assertionData,
        {
          headers: {
            'X-Session-ID': sessionId
          }
        }
      )

      // Clear abort controller on success
      this.conditionalAbortController = null

      return {
        success: true,
        data: finishResponse.data
      }
    } catch (error) {
      // Don't treat abort as error
      if (error.name === 'AbortError') {
        return {
          success: false,
          aborted: true,
          error,
          errorKey: WebAuthnErrors.AbortError
        }
      }
      return {
        success: false,
        error,
        errorKey: getWebAuthnErrorKey(error)
      }
    }
  }

  /**
   * Abort any pending conditional login request
   * Call this when user starts typing email or uses another login method
   */
  abortConditionalLogin() {
    if (this.conditionalAbortController) {
      this.conditionalAbortController.abort()
      this.conditionalAbortController = null
    }
  }

  /**
   * Register a new passkey (2-step process)
   * @param {string} credentialName - Optional name for the credential
   * @returns {Promise<Object>}
   */
  async register(credentialName = null) {
    try {
      // Step 1: Begin registration
      const beginResponse = await this.$axios.post('/proxyapi/auth/webauthn/register/begin')
      const { options, session_id: sessionId } = beginResponse.data

      // Step 2: Create credential
      const parsedOptions = prepareCredentialCreationOptions(options)
      const credential = await navigator.credentials.create(parsedOptions)

      if (!credential) {
        throw new Error('Failed to create credential')
      }

      // Step 3: Finish registration
      const credentialData = formatRegistrationCredential(credential)

      // Build URL with credential_name as query parameter if provided
      let finishUrl = '/proxyapi/auth/webauthn/register/finish'
      if (credentialName) {
        finishUrl += `?credential_name=${encodeURIComponent(credentialName)}`
      }

      const finishResponse = await this.$axios.post(finishUrl, credentialData)

      return {
        success: true,
        data: finishResponse.data,
        sessionId
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: getWebAuthnErrorKey(error)
      }
    }
  }

  /**
   * Login with passkey (2-step process)
   * @param {string} email - User email
   * @param {number} timeout - Timeout in milliseconds (default: 60000ms)
   * @returns {Promise<Object>}
   */
  async login(email, timeout = 60000) {
    try {
      // Step 1: Begin login
      const beginResponse = await this.$axios.post('/proxyapi/webauthn/login/begin', {
        email: email.trim()
      })
      const { options, session_id: sessionId } = beginResponse.data

      // Step 2: Get assertion with timeout
      const parsedOptions = prepareCredentialRequestOptions(options)

      // Create a promise that rejects after timeout
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const timeoutPromise = new Promise((resolve, reject) => {
        setTimeout(() => {
          reject(new Error('WebAuthn timeout - passkey prompt did not respond'))
        }, timeout)
      })

      // Race between the credential request and the timeout
      const assertion = await Promise.race([
        navigator.credentials.get(parsedOptions),
        timeoutPromise
      ])

      if (!assertion) {
        throw new Error('Failed to get assertion')
      }

      // Step 3: Finish login
      const assertionData = formatLoginAssertion(assertion)

      // Build URL with email as query parameter
      const finishUrl = `/proxyapi/webauthn/login/finish?email=${encodeURIComponent(email.trim())}`

      const finishResponse = await this.$axios.post(finishUrl, assertionData)

      return {
        success: true,
        data: finishResponse.data,
        sessionId
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: getWebAuthnErrorKey(error)
      }
    }
  }

  /**
   * Check if user has registered passkeys
   * @param {string} email - User email
   * @returns {Promise<boolean>}
   */
  async hasPasskey(email) {
    try {
      const response = await this.$axios.post('/proxyapi/webauthn/login/begin', {
        email: email.trim()
      })
      return response.status === 200
    } catch (error) {
      // 404 means no passkeys registered
      if (error.response?.status === 404) {
        return false
      }
      // Other errors might indicate user doesn't exist or other issues
      return false
    }
  }

  /**
   * Get list of registered passkeys
   * @returns {Promise<Object>}
   */
  async getCredentials() {
    try {
      const response = await this.$axios.get('/proxyapi/auth/webauthn/credentials')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: WebAuthnErrors.NetworkError
      }
    }
  }

  /**
   * Delete a passkey
   * @param {string} credentialId - Base64 encoded credential ID
   * @returns {Promise<Object>}
   */
  async deleteCredential(credentialId) {
    try {
      const response = await this.$axios.post('/proxyapi/auth/webauthn/credentials/delete', {
        credential_id: credentialId
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: WebAuthnErrors.NetworkError
      }
    }
  }

  /**
   * Rename a passkey
   * @param {string} credentialId - Base64 encoded credential ID
   * @param {string} newName - New name for the credential
   * @returns {Promise<Object>}
   */
  async renameCredential(credentialId, newName) {
    try {
      const response = await this.$axios.post('/proxyapi/auth/webauthn/credentials/rename', {
        credential_id: credentialId,
        new_name: newName
      })
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: WebAuthnErrors.NetworkError
      }
    }
  }

  /**
   * Check if user has any registered passkeys
   * @returns {Promise<boolean>}
   */
  async hasPasskeyRegistered() {
    try {
      const response = await this.$axios.get('/proxyapi/auth/webauthn/credentials')
      const credentials = response.data || []
      return Array.isArray(credentials) && credentials.length > 0
    } catch (error) {
      // 404 or error means no passkeys
      return false
    }
  }

  /**
   * Dismiss passkey onboarding prompt (save preference on backend)
   * @returns {Promise<Object>}
   */
  async dismissPasskeyPrompt() {
    try {
      const response = await this.$axios.post('/proxyapi/auth/dismiss-passkey-prompt')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: WebAuthnErrors.NetworkError
      }
    }
  }

  /**
   * Complete onboarding (sets first_login = false on backend)
   * @returns {Promise<Object>}
   */
  async completeOnboarding() {
    try {
      const response = await this.$axios.post('/proxyapi/auth/complete-onboarding')
      return {
        success: true,
        data: response.data
      }
    } catch (error) {
      return {
        success: false,
        error,
        errorKey: WebAuthnErrors.NetworkError
      }
    }
  }
}

/**
 * Nuxt plugin installer
 */
export default ({ $axios, $auth }, inject) => {
  // Inject WebAuthn service (with $auth for user-specific localStorage keys)
  const webauthnService = new WebAuthnService($axios, $auth)
  inject('webauthn', webauthnService)

  // Inject utility functions
  inject('webauthnUtils', {
    isAvailable: isWebAuthnAvailable,
    isConditionalMediationSupported,
    isInAppBrowser,
    base64urlEncode,
    base64urlDecode,
    getErrorKey: getWebAuthnErrorKey
  })
}
